import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { request, onApiFailure } from '@papan/api-client'

/**
 * #853 — a FOLLOWED app link the reader cannot open as a member.
 *
 * `/t/<tenant>/app/<id>` is the named link every page, email and redirect now writes.
 * The owner opens it as their own drive; a MEMBER of that tenant opens it there; but
 * a reader who reaches the app through a `.shared` in their OWN drive is refused by
 * that tenant (403 `tenant_access_denied`) — and was shown "You don't have access"
 * although their drive opens the app. So before that screen, for an `/app/` route
 * only, ask where THIS reader opens it (`GET /api/miniapp/:id/open-path`, #807's
 * `nativeSpaPath`) and go there instead.
 *
 * Asked AS the reader's OWN personal drive, never the URL's tenant — that tenant is
 * the one that just refused them. An answer back into the refused tenant (they hold
 * no share of it) is NOT followed: that would loop; the screen shows as before.
 */
/** How long a refusal waits for the reader's identity before the screen shows anyway. */
const IDENTITY_WAIT_MS = 8_000

const APP_ROUTE = /^\/t\/([A-Za-z0-9_-]{1,64})\/app\/([A-Za-z0-9_-]{1,64})(\/[^?#]*)?$/

/** Is `pathname` in the reader's OWN personal drive? A 403 `tenant_access_denied` here is
 *  EITHER a straggler from a tenant the reader just left (its other calls, React Query's
 *  retry) OR real — the cookie changed account in another tab (#341). Timing cannot tell
 *  them apart, so the hook asks the server ({@link ownDriveRefused}). */
export function isOwnDriveRoute(pathname: string, readerPersonalTenantId: string | null | undefined): boolean {
  if (pathname === '/~' || pathname.startsWith('/~/')) return true
  return !!readerPersonalTenantId
    && (pathname === `/t/${readerPersonalTenantId}` || pathname.startsWith(`/t/${readerPersonalTenantId}/`))
}

/** How long the own-drive probe may take before it counts as "cannot tell". */
const PROBE_TIMEOUT_MS = 10_000

/** Is the reader's own drive REALLY refused? One tenant-scoped read as that drive. Only a
 *  SUCCESS proves it works — the refusal that raised the question was a straggler. A 401 is
 *  the session dying, which its own handling shows (#501). Anything else — the 403 itself
 *  (#341), a 5xx / 429, no answer, or no answer in time — cannot prove the drive works, so it
 *  answers "refused": a real refusal is never hidden (the behaviour before #853). */
export async function ownDriveRefused(readerPersonalTenantId: string): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timedOut = new Promise<'timeout'>((resolve) => { timer = setTimeout(() => resolve('timeout'), PROBE_TIMEOUT_MS) })
  try {
    const answer = await Promise.race([
      request('/api/files/write-status', { headers: { 'x-tenant-id': readerPersonalTenantId } }).then(() => 'ok' as const),
      timedOut,
    ])
    return answer !== 'ok'
  } catch (err) {
    return (err as { status?: number })?.status !== 401
  } finally {
    clearTimeout(timer)
  }
}

export function isDeniableAppRoute(pathname: string): boolean {
  return APP_ROUTE.test(pathname)
}

export async function deniedAppRedirect(
  pathname: string,
  search: string,
  readerPersonalTenantId: string | null | undefined,
): Promise<string | null> {
  const m = APP_ROUTE.exec(pathname)
  if (!m || !readerPersonalTenantId) return null
  const tenant = m[1]!
  const folder = m[2]!
  const tail = m[3] ?? ''
  if (tenant === readerPersonalTenantId) return null
  try {
    const res = await request<{ spaPath?: unknown }>(
      `/api/miniapp/${encodeURIComponent(folder)}/open-path?tenantId=${encodeURIComponent(tenant)}`,
      { headers: { 'x-tenant-id': readerPersonalTenantId } },
    )
    const to = res?.spaPath
    if (typeof to !== 'string' || !to.startsWith('/') || to.startsWith('//') || to.startsWith('/\\')) return null
    if (to === `/t/${tenant}` || to.startsWith(`/t/${tenant}/`)) return null
    return `${to}${tail}${search}`
  } catch {
    return null
  }
}

/**
 * The shell's "this tenant rejected us" state (#341), shared by the desktop and mobile
 * shells so they cannot drift: set on a 403 `tenant_access_denied` from ANY
 * tenant-scoped call, reset on every tenant switch — and, for a followed app link, the
 * reader's own way in is tried first (above). The #501 session-death 401s carry no
 * `code` and never set it.
 */
export function useTenantDenied(
  tenantId: string,
  personalTenantId: string | null | undefined,
): [boolean, (denied: boolean) => void] {
  const [denied, setDenied] = useState(false)
  const navigate = useNavigate()
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate
  const personalRef = useRef(personalTenantId)
  personalRef.current = personalTenantId
  // The app route already being resolved — the burst of failing calls it triggers
  // asks once, not once per call.
  const attempted = useRef<string | null>(null)
  // A refusal that arrived BEFORE the reader's identity loaded: a cold `/t/<other>/…`
  // visit fires that tenant's requests while the session is still loading, so the first
  // 403 can precede `personalTenantId`. Held, and tried the moment it is known.
  const pending = useRef<{ pathname: string; search: string } | null>(null)
  // An own-drive refusal being checked with the server (its own 403 must not re-ask).
  const checkingOwnDrive = useRef(false)
  // Which probe owns that flag: only the LATEST. The tenant switch below clears the flag
  // while a probe may be in flight; a later probe then holds it, and the earlier one must
  // neither release it (the later probe's own 403 would start another probe, and so on)
  // nor decide over the later one's answer.
  const probeGen = useRef(0)

  const tryOwnWayIn = useRef((pathname: string, search: string) => {
    void deniedAppRedirect(pathname, search, personalRef.current).then((to) => {
      // The reader already left the link they followed: a late answer changes nothing —
      // wherever they are now answers for itself.
      if (window.location.pathname !== pathname) return
      if (to) navigateRef.current(to, { replace: true })
      else setDenied(true)
    })
  })

  useEffect(() => onApiFailure((failure) => {
    if (failure.status !== 403 || failure.code !== 'tenant_access_denied') return
    // D134: a refusal of a request PINNED to an earlier identity (an undo begun before a switch) speaks about that
    // identity, not this session — never the access-denied screen for whoever is signed in now
    if (failure.staleIdentity) return
    const { pathname, search } = window.location
    // On the reader's OWN drive a refusal is either a straggler from a tenant just left
    // (after our redirect or their own click) or real (#341, account changed elsewhere).
    // The server decides: one probe as that drive — refused → the screen; answered → ignore.
    if (isOwnDriveRoute(pathname, personalRef.current)) {
      const me = personalRef.current
      if (!me) { setDenied(true); return }
      if (checkingOwnDrive.current) return
      checkingOwnDrive.current = true
      const gen = ++probeGen.current
      void ownDriveRefused(me).then((refused) => {
        if (probeGen.current !== gen) return
        checkingOwnDrive.current = false
        // Still the same reader, still on their own drive — a probe about someone else
        // (the session changed account meanwhile) decides nothing.
        if (refused && personalRef.current === me && isOwnDriveRoute(window.location.pathname, me)) setDenied(true)
      })
      return
    }
    if (!isDeniableAppRoute(pathname)) { setDenied(true); return }
    if (attempted.current === pathname) return
    attempted.current = pathname
    if (!personalRef.current) {
      // …and never left hanging: if the identity does not arrive, the access-denied
      // screen shows as it always did. The timer is armed by THIS refusal, so a late
      // one waits its full turn too.
      const held = { pathname, search }
      pending.current = held
      setTimeout(() => {
        if (pending.current === held) { pending.current = null; setDenied(true) }
      }, IDENTITY_WAIT_MS)
      return
    }
    tryOwnWayIn.current(pathname, search)
  }), [])

  // The identity arrived: try a held refusal now.
  useEffect(() => {
    const held = pending.current
    if (!held || !personalTenantId) return
    pending.current = null
    tryOwnWayIn.current(held.pathname, held.search)
  }, [personalTenantId])

  // A denial captured for the PREVIOUS tenant must not linger; if the new one is
  // also denied, its own failing calls put it right back.
  useEffect(() => {
    setDenied(false)
    attempted.current = null
    pending.current = null
    checkingOwnDrive.current = false
  }, [tenantId])

  return [denied, setDenied]
}
